import { nanoid } from 'nanoid/non-secure';
import { shopifyAppProxyRequest, rechargeApiRequest, getInternalSession } from '../utils/request.js';
import { getOptions } from '../utils/options.js';
import { toLineItemProperty, BundleSelectionValidator } from '../utils/bundle.js';

const STORE_FRONT_MANAGER_URL = "/bundling-storefront-manager";
function getTimestampSecondsFromClient() {
  return Math.ceil(Date.now() / 1e3);
}
async function getTimestampSecondsFromServer() {
  try {
    const { timestamp } = await shopifyAppProxyRequest("get", `${STORE_FRONT_MANAGER_URL}/t`);
    return timestamp;
  } catch (ex) {
    console.error(`Fetch failed: ${ex}. Using client-side date.`);
    return getTimestampSecondsFromClient();
  }
}
async function getBundleId(bundle) {
  const opts = getOptions();
  const isValid = await validateBundle(bundle);
  if (isValid !== true) {
    throw new Error(isValid);
  }
  const timestampSeconds = await getTimestampSecondsFromServer();
  const bundleData = toLineItemProperty({
    variantId: bundle.externalVariantId,
    version: timestampSeconds,
    items: bundle.selections.map((item) => {
      return {
        collectionId: item.collectionId,
        productId: item.externalProductId,
        variantId: item.externalVariantId,
        quantity: item.quantity,
        sku: ""
      };
    })
  });
  try {
    const payload = await shopifyAppProxyRequest(
      "post",
      `${STORE_FRONT_MANAGER_URL}/api/v1/bundles`,
      {
        data: {
          bundle: bundleData
        },
        headers: {
          Origin: `https://${opts.storeIdentifier}`
        }
      }
    );
    if (!payload.id || payload.code !== 200) {
      throw new Error(`1: failed generating rb_id: ${JSON.stringify(payload)}`);
    }
    return payload.id;
  } catch (e) {
    throw new Error(`2: failed generating rb_id ${e}`);
  }
}
function getDynamicBundleItems(bundle, shopifyProductHandle) {
  const isValid = validateDynamicBundle(bundle);
  if (isValid !== true) {
    throw new Error(`Dynamic Bundle is invalid. ${isValid}`);
  }
  const bundleId = `${nanoid(9)}:${bundle.externalProductId}`;
  return bundle.selections.map((item) => {
    const itemData = {
      id: item.externalVariantId,
      quantity: item.quantity,
      properties: {
        _rc_bundle: bundleId,
        _rc_bundle_variant: bundle.externalVariantId,
        _rc_bundle_parent: shopifyProductHandle,
        _rc_bundle_collection_id: item.collectionId
      }
    };
    if (item.sellingPlan) {
      itemData.selling_plan = item.sellingPlan;
    } else if (item.shippingIntervalFrequency) {
      itemData.properties.shipping_interval_frequency = item.shippingIntervalFrequency;
      itemData.properties.shipping_interval_unit_type = item.shippingIntervalUnitType;
      itemData.id = `${item.discountedVariantId}`;
    }
    return itemData;
  });
}
async function validateBundle(bundle) {
  try {
    if (!bundle) {
      return "Bundle is not defined";
    }
    return true;
  } catch (e) {
    return `Error fetching bundle settings: ${e}`;
  }
}
async function validateBundleSelection(bundle) {
  try {
    const bundleData = await shopifyAppProxyRequest("get", `/bundle-data/${bundle.externalProductId}`);
    const selectionValidator = new BundleSelectionValidator(bundle.selections, bundleData);
    return { valid: selectionValidator.isBundleSelectionValid(bundle.externalVariantId) };
  } catch (error) {
    return { valid: false, error: String(error) };
  }
}
const intervalUnitGroups = {
  day: ["day", "days", "Days"],
  days: ["day", "days", "Days"],
  Days: ["day", "days", "Days"],
  week: ["week", "weeks", "Weeks"],
  weeks: ["week", "weeks", "Weeks"],
  Weeks: ["week", "weeks", "Weeks"],
  month: ["month", "months", "Months"],
  months: ["month", "months", "Months"],
  Months: ["month", "months", "Months"]
};
function validateDynamicBundle(bundle) {
  if (!bundle) {
    return "No bundle defined.";
  }
  if (bundle.selections.length === 0) {
    return "No selections defined.";
  }
  const { shippingIntervalFrequency, shippingIntervalUnitType } = bundle.selections.find((selection) => selection.shippingIntervalFrequency || selection.shippingIntervalUnitType) || {};
  if (shippingIntervalFrequency || shippingIntervalUnitType) {
    if (!shippingIntervalFrequency || !shippingIntervalUnitType) {
      return "Shipping intervals do not match on selections.";
    } else {
      const shippingIntervalUnitGroup = intervalUnitGroups[shippingIntervalUnitType];
      for (let x = 0; x < bundle.selections.length; x++) {
        const { shippingIntervalFrequency: frequency, shippingIntervalUnitType: unitType } = bundle.selections[x];
        if (frequency && frequency !== shippingIntervalFrequency || unitType && !shippingIntervalUnitGroup.includes(unitType)) {
          return "Shipping intervals do not match on selections.";
        }
      }
    }
  }
  return true;
}
async function getBundleSelection(session, id) {
  const { bundle_selection } = await rechargeApiRequest(
    "get",
    `/bundle_selections`,
    {
      id
    },
    getInternalSession(session, "getBundleSelection")
  );
  return bundle_selection;
}
function listBundleSelections(session, query) {
  return rechargeApiRequest(
    "get",
    `/bundle_selections`,
    { query },
    getInternalSession(session, "listBundleSelections")
  );
}
async function createBundleSelection(session, createRequest) {
  const { bundle_selection } = await rechargeApiRequest(
    "post",
    `/bundle_selections`,
    {
      data: createRequest
    },
    getInternalSession(session, "createBundleSelection")
  );
  return bundle_selection;
}
async function updateBundleSelection(session, id, updateRequest) {
  const { bundle_selection } = await rechargeApiRequest(
    "put",
    `/bundle_selections`,
    {
      id,
      data: updateRequest
    },
    getInternalSession(session, "updateBundleSelection")
  );
  return bundle_selection;
}
function deleteBundleSelection(session, id) {
  return rechargeApiRequest(
    "delete",
    `/bundle_selections`,
    {
      id
    },
    getInternalSession(session, "deleteBundleSelection")
  );
}
async function updateBundle(session, purchase_item_id, updateRequest, query) {
  const { subscription } = await rechargeApiRequest(
    "put",
    "/bundles",
    {
      id: purchase_item_id,
      data: updateRequest,
      query
    },
    getInternalSession(session, "updateBundle")
  );
  return subscription;
}

export { createBundleSelection, deleteBundleSelection, getBundleId, getBundleSelection, getDynamicBundleItems, listBundleSelections, updateBundle, updateBundleSelection, validateBundle, validateBundleSelection, validateDynamicBundle };
//# sourceMappingURL=bundle.js.map
